/**
 * Billing Details Utility
 *
 * Calculates billing cycle label and next billing date
 * for membership and pass subscription plans.
 *
 * @module butterUI/utils/billingDetails
 */

import { formatClubDate, asUtcDate, type Timezone } from '@butter/library/datetime';
import { addMonthsClamped } from '@butter/library/subscription/calculators';
import type { FormattedSubscriptionPlan } from '@lgc/database/types/domains/subscription';

export interface BillingDetails {
  /** Formatted start date (e.g., "February 26, 2026") */
  startDate: string;
  /** Human-readable billing cycle (e.g., "Monthly", "Quarterly", "Annually", "One-Time", "30 Days") */
  billingCycle: string;
  /** Formatted next billing date, or empty string for one-time purchases */
  nextBillingDate: string;
  /** Next billing date as Date object (for confirmation page calculations) */
  nextBillingDateObj?: Date;
}

/**
 * User subscription data for billing calculations (confirmation page)
 */
export interface UserSubscriptionData {
  subscriptionType: 'membership' | 'pass';
  billingFrequency?: string;
  validityDays?: number;
  startDate: string; // ISO date string
  expirationDate?: string; // ISO date string (for passes)
}

/**
 * Calculate billing details for a subscription plan (purchase flow).
 *
 * Handles all BillingFrequency values ('monthly', 'quarterly', 'annually', 'one-time')
 * and pass validity days. Returns an empty `nextBillingDate` for one-time memberships.
 *
 * @param selectedSubscription - The selected subscription plan
 * @param timezone - Club timezone for date formatting
 * @returns Formatted billing details ready for display
 *
 * @example
 * ```ts
 * const details = calcBillingDetails(selectedSubscription, club.timezone);
 * // { startDate: "February 26, 2026", billingCycle: "Quarterly", nextBillingDate: "May 26, 2026" }
 * ```
 */
export function calcBillingDetails(
  selectedSubscription: FormattedSubscriptionPlan,
  timezone: Timezone
): BillingDetails;

/**
 * Calculate billing details for a user subscription (confirmation page).
 *
 * Uses actual start date from user subscription data rather than "today".
 * Handles all billing frequencies and pass validity days.
 *
 * @param userSubscription - User subscription data with start date
 * @param timezone - Club timezone for date formatting
 * @returns Formatted billing details ready for display
 *
 * @example
 * ```ts
 * const details = calcBillingDetails(
 *   { 
 *     subscriptionType: 'membership',
 *     billingFrequency: 'quarterly',
 *     startDate: '2026-02-26T00:00:00Z'
 *   },
 *   'America/Denver'
 * );
 * // { startDate: "February 26, 2026", billingCycle: "Quarterly", nextBillingDate: "May 26, 2026" }
 * ```
 */
export function calcBillingDetails(
  userSubscription: UserSubscriptionData,
  timezone: Timezone
): BillingDetails;

/**
 * Implementation of calcBillingDetails (handles both overloads)
 */
export function calcBillingDetails(
  subscription: FormattedSubscriptionPlan | UserSubscriptionData,
  timezone: Timezone
): BillingDetails {
  // Determine if this is a purchase plan or user subscription
  const isUserSubscription = 'startDate' in subscription && typeof subscription.startDate === 'string';
  
  // Get start date
  const startDateObj = isUserSubscription 
    ? new Date((subscription as UserSubscriptionData).startDate)
    : new Date();
  
  const startDate = formatClubDate(asUtcDate(startDateObj), timezone, 'MMMM d, yyyy');
  
  // Calculate next billing/expiration date
  let nextDate = new Date(startDateObj);
  let billingCycle = '';

  if (subscription.subscriptionType === 'membership') {
    const frequency = subscription.billingFrequency;
    // Use a switch with explicit default so unknown/undefined frequencies
    // don't silently set nextBillingDate to today.
    switch (frequency) {
      case 'monthly':
        nextDate = addMonthsClamped(nextDate, 1);
        billingCycle = 'Monthly';
        break;
      case 'quarterly':
        nextDate = addMonthsClamped(nextDate, 3);
        billingCycle = 'Quarterly';
        break;
      case 'semi-annually':
        nextDate = addMonthsClamped(nextDate, 6);
        billingCycle = 'Semi-Annual';
        break;
      case 'annually':
      case 'yearly': // Handle both variants
      case 'annual': // Handle both variants
        nextDate = addMonthsClamped(nextDate, 12);
        billingCycle = 'Annually';
        break;
      case 'one-time':
      default:
        // Treat unknown/undefined as one-time — safest fallback
        billingCycle = 'One-Time';
        break;
    }
  } else {
    // Passes - use validity days
    const validityDays = subscription.validityDays || 30;
    nextDate.setDate(nextDate.getDate() + validityDays);
    billingCycle = `${validityDays} Days`;
  }

  // Explicit allowlist: only known recurring frequencies qualify.
  // Passes are one-time purchases and don't have next billing dates.
  // Only memberships with recurring billing frequencies should show next billing date.
  const frequency = subscription.billingFrequency;
  const isRecurring =
    subscription.subscriptionType === 'membership' &&
    (frequency === 'monthly' ||
     frequency === 'quarterly' ||
     frequency === 'semi-annually' ||
     frequency === 'annually' ||
     frequency === 'yearly' ||
     frequency === 'annual');

  const nextBillingDate = isRecurring
    ? formatClubDate(asUtcDate(nextDate), timezone, 'MMMM d, yyyy')
    : '';

  return { 
    startDate, 
    billingCycle, 
    nextBillingDate,
    nextBillingDateObj: isRecurring ? nextDate : undefined,
  };
}
